/**
 * My notes tab (spec 05 card 6): every note on this meeting with its quote and timestamp. Clicking a
 * note jumps the video there and opens the transcript at that line.
 */
import { useState } from "react";
import { Download, Pencil, Play, Trash2 } from "lucide-react";
import { notesStore, useNotes } from "../../stores/notesStore";
import { meetingStore, useMeeting } from "../../stores/meetingStore";
import { clock } from "../../lib/format";
import { seekTo } from "./vimeoClock";
import { List, ListRow, RowActions, RowLead, RowMain } from "../shared/List";
import type { Note } from "../../types";

function exportNotes(notes: Note[], title: string) {
  const lines = [`# ${title}`, ""];
  for (const n of notes) {
    lines.push(`${clock(n.t ?? 0)}  “${n.quote ?? ""}”${n.speaker ? ` (${n.speaker})` : ""}`);
    if (n.text) lines.push(n.text);
    lines.push("");
  }
  const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: `${title.replace(/[^\w -]+/g, "").trim() || "notes"}.txt` });
  a.click();
  URL.revokeObjectURL(url);
}

function Row({ note }: { note: Note }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(note.text ?? "");
  const open = () => {
    meetingStore.getState().setTranscriptHidden(false);
    seekTo(note.t ?? 0);
    dispatchEvent(new Event("transcript:live"));
  };
  return (
    <ListRow className="note-row">
      <RowLead><button className="note-time" onClick={open}><Play size={11} />{clock(note.t ?? 0)}</button></RowLead>
      <RowMain className="note-body">
        <p className="note-quote-line">
          <mark className="note-quote" data-hl={note.color}>“{note.quote}”</mark>
          {note.speaker && <span className="note-speaker">— {note.speaker}</span>}
        </p>
        {editing ? (
          <form className="note-edit" onSubmit={(e) => {
            e.preventDefault();
            notesStore.getState().editNote(note);
            void notesStore.getState().save(text.trim(), note.visibility);
            setEditing(false);
          }}>
            <textarea aria-label="Note" rows={2} value={text} autoFocus onChange={(e) => setText(e.target.value)} />
            <div className="note-actions">
              <button type="button" className="note-cancel" onClick={() => setEditing(false)}>Cancel</button>
              <button className="btn btn--primary btn--small">Save note</button>
            </div>
          </form>
        ) : note.text ? <p className="note-text">{note.text}</p> : null}
      </RowMain>
      {note.mine !== false && (
        <RowActions className="note-tools">
          <button className="icon-btn" aria-label="Edit note" onClick={() => setEditing(true)}><Pencil size={14} /></button>
          <button className="icon-btn" aria-label="Delete note" onClick={() => void notesStore.getState().remove(note.id)}><Trash2 size={14} /></button>
        </RowActions>
      )}
    </ListRow>
  );
}

export function MyNotes() {
  const notes = useNotes((s) => s.notes).filter((n) => n.mine !== false);
  const title = useMeeting((s) => (s.meeting ? `${s.meeting.body} ${s.meeting.startsAt.slice(0, 10)} notes` : "Notes"));
  const sorted = [...notes].sort((a, b) => (a.t ?? 0) - (b.t ?? 0));
  const shared = notes.some((n) => n.visibility === "team");
  return (
    <section className="my-notes tab-panel" role="tabpanel">
      <header className="my-notes-head">
        <span>{notes.length} {notes.length === 1 ? "note" : "notes"}{shared ? "" : " · only visible to you"}</span>
        <button className="btn" onClick={() => exportNotes(sorted, title)} disabled={!notes.length}><Download size={14} />Export notes</button>
      </header>
      <List as="ol" className="note-list">{sorted.map((n) => <Row key={n.id} note={n} />)}</List>
    </section>
  );
}
